"use client";

import { useMemo, useState } from "react";
import { Checkbox } from "@/app/_module/components/ui/checkbox";
import { Input } from "@/app/_module/components/ui/input";
import { groupCustomFieldOptions } from "@/app/_module/utils/customFieldAliases";

/** Column key for a custom field: "cf:" + the picker key ("cf:Carrier", "cf:Carrier 2"). */
export const CF_COLUMN_PREFIX = "cf:";
export const cfColumnKey = (pickerKey: string) => `${CF_COLUMN_PREFIX}${pickerKey}`;
export const cfColumnLabel = (columnKey: string) => columnKey.slice(CF_COLUMN_PREFIX.length);
export const isCfColumn = (columnKey: string) => columnKey.startsWith(CF_COLUMN_PREFIX);

/**
 * Custom fields as table columns. Same grouped list the filters use, so
 * "Carrier" here is the same alias group as "Carrier" there. Used by the Leads
 * Filters → Columns panel and Settings → Preferences → Tables.
 */
export function CustomFieldColumnPicker({
  fields,
  selected,
  onToggle,
  disabled,
}: {
  fields: Array<{ key: string; type: string }>;
  selected: string[];
  onToggle: (columnKey: string, on: boolean) => void;
  disabled?: boolean;
}) {
  const [q, setQ] = useState("");
  const options = useMemo(() => groupCustomFieldOptions(fields), [fields]);
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? options.filter((o) => o.key.toLowerCase().includes(needle)) : options;
  }, [options, q]);
  const picked = selected.filter(isCfColumn).length;

  if (options.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between pt-1">
        <span className="text-xs font-medium text-muted-foreground">Custom fields{picked ? ` · ${picked} shown` : ""}</span>
      </div>
      {options.length > 8 && (
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a field…" className="h-7 text-xs" />
      )}
      <div className="max-h-44 space-y-0.5 overflow-y-auto pr-1">
        {shown.map((o) => {
          const key = cfColumnKey(o.key);
          return (
            <label key={key} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 hover:bg-muted/50">
              <Checkbox checked={selected.includes(key)} disabled={disabled} onCheckedChange={(c) => onToggle(key, c === true)} />
              <span className="text-sm">{o.key}</span>
            </label>
          );
        })}
        {shown.length === 0 && <p className="px-1 py-1 text-xs text-muted-foreground">No field matches.</p>}
      </div>
    </div>
  );
}
